import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import navigation2Fill from '@iconify/icons-eva/navigation-2-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { TOUR_Z_INDEX } from './tourStyles';
import { useTour } from './TourProvider';

const WIDTH = 300;
const GAP = 12;
// Rough popover height, used only to decide whether it fits below the anchor.
const ESTIMATED_HEIGHT = 92;

/**
 * Shown while an interactive guide is running but the user is not yet on the
 * page the current stage belongs to.
 *
 * Where possible it points at whatever the user needs to click next -- the
 * stage's `waitingAnchor`, falling back to a list of candidates so the same
 * stage can anchor to an "Add New" button on one page and the sidebar entry on
 * another. With no anchor on screen it sits bottom-centre as before.
 */
function findAnchor(anchors) {
  for (const anchor of anchors) {
    // Anchors may be a plain target name or { target, hint }.
    const target = typeof anchor === 'string' ? anchor : anchor.target;
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      // Skip anything scrolled out of view or hidden.
      if (rect.width && rect.height && rect.bottom > 0 && rect.top < window.innerHeight) {
        return { rect, hint: typeof anchor === 'string' ? null : anchor.hint };
      }
    }
  }
  return null;
}

export default function GuideBanner() {
  const { activeGuide, stopGuide } = useTour();
  const [found, setFound] = useState(null);

  const anchors = activeGuide?.waitingAnchors;
  const isWaiting = Boolean(activeGuide) && !activeGuide.isOnGuidePage;

  useEffect(() => {
    if (!isWaiting || !anchors?.length) {
      setFound(null);
      return undefined;
    }

    // The target may mount late (sidebar, async page), so keep re-measuring.
    const measure = () => setFound(findAnchor(anchors));
    measure();
    const timer = window.setInterval(measure, 500);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isWaiting, anchors]);

  if (!isWaiting) return null;

  // Anchored: sit just below the target, left-aligned to it. Buttons usually
  // sit in a row, so going right would cover whatever is next to them.
  const rect = found?.rect;
  // The matched anchor's own wording wins, so the hint always describes what
  // the user can actually see on this page.
  const hint = found?.hint || activeGuide.waitingHint || 'Carry on to the next page to continue.';

  let position;
  if (rect) {
    // Sidebar entries are tall and narrow and live against the screen edge, so
    // sitting beneath one would overhang it. Those get the banner to their
    // right; page buttons get it underneath, clear of their neighbours.
    const isNarrowSideAnchor = rect.left < WIDTH && rect.width < 300;

    if (isNarrowSideAnchor) {
      position = {
        left: Math.min(rect.right + GAP, window.innerWidth - WIDTH - GAP),
        top: Math.min(Math.max(GAP, rect.top), window.innerHeight - ESTIMATED_HEIGHT - GAP),
      };
    } else {
      // Aligned to the anchor's left edge, kept on screen.
      const left = Math.max(GAP, Math.min(rect.left, window.innerWidth - WIDTH - GAP));
      // Below the anchor, unless that would run off the bottom -- then above.
      const below = rect.bottom + GAP;
      const top =
        below + ESTIMATED_HEIGHT > window.innerHeight
          ? Math.max(GAP, rect.top - GAP - ESTIMATED_HEIGHT)
          : below;
      position = { left, top };
    }
  } else {
    position = { left: '50%', bottom: 24, transform: 'translateX(-50%)' };
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        ...position,
        width: rect ? WIDTH : 'auto',
        zIndex: TOUR_Z_INDEX - 1,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        maxWidth: 'calc(100vw - 32px)',
        borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ color: 'primary.main', display: 'flex', mt: 0.25 }}>
          <Icon icon={navigation2Fill} width={20} height={20} />
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {activeGuide.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', whiteSpace: 'normal' }}
          >
            {hint}
          </Typography>
        </Box>
        <IconButton size="small" onClick={stopGuide} aria-label="stop guide">
          <Icon icon={closeFill} width={18} height={18} />
        </IconButton>
      </Stack>
    </Paper>
  );
}
